import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useForm from 'shared/hooks/useForm';
import { DEFAULT_LANGUAGE_CODE, DEFAULT_LANGUAGE_ID } from 'shared/constants';
import mapEntitiesToOptions from 'shared/lib/mapEntitiesToOptions';
import TitleWithButton from 'shared/components/TitleWithButton';
import LanguageSwitch from 'shared/components/LanguageSwitch';
import Subheader from 'shared/components/Subheader';
import { Submit, Select, Checkbox } from 'shared/components/Form';
import Tip from 'shared/components/Tip/Tip';
import Page from 'userportal/components/Page';
// import apiClient from 'userportal/lib/ApiClient';
import { actions as authActions, selectors as authSelectors } from 'userportal/ducks/auth';
import { selectors as fieldSelectors } from 'userportal/ducks/fields';
import { selectors as langSelectors } from 'userportal/ducks/languages';
import {
  actions as profileActions,
  helpers as profileHelpers,
  selectors as profileSelectors
} from 'userportal/ducks/profile';
import { userportal as routes } from 'shared/routes';
import styles from './Signup.module.scss';

export const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fields = mapEntitiesToOptions(useSelector(fieldSelectors.getAllFields), 'id', 'slug');
  const languages = mapEntitiesToOptions(useSelector(langSelectors.geti18nLanguages), 'code', 'slug');
  const allLanguages = mapEntitiesToOptions(useSelector(langSelectors.getAllLanguages), 'id', 'slug');
  const user = useSelector(authSelectors.getUser);
  const userProfile = useSelector(profileSelectors.getUserProfile);

  const profile = {
    firstname: user.firstname,
    lastname: user.lastname,
    ...userProfile,
  };

  const { i18n, t } = useTranslation('userportal');
  const hasSufficientSkeleton = profile.primaryFieldId && profile.languages.ids.length > 0;

  const skillsCount = profile.skills && profile.skills.ids ? profile.skills.ids.length : 0;
  useEffect(() => {
    if (hasSufficientSkeleton) {
      if (skillsCount > 0) {
        navigate(routes.root, { replace: true });
      } else {
        navigate(routes.user.profile.view, { replace: true });
      }
    }
  }, [hasSufficientSkeleton, skillsCount]);

  const fieldsList = fields.map(field => ({ value: field.value, label: t(`common:field.${field.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const languagesList = languages
    .map(language => ({ value: language.value, label: t(`common:language.${language.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const allLanguagesList = allLanguages
    .map(language => ({ value: language.value, label: t(`common:language.${language.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleChangeLanguage = lang => dispatch(authActions.setLanguage(lang));

  const onSubmit = async (data) => {
    const payload = {
      language: data.language,
      primaryFieldId: data.primaryFieldId,
      languages: {
        ids: [data.nativeLanguage],
        entities: {
          [data.nativeLanguage]: {
            languageId: data.nativeLanguage,
            levelId: 6
          }
        }
      }
    };
    dispatch(authActions.setLanguage(data.language));
    dispatch(profileActions.updateProfile(profileHelpers.denormalizeProfile({ ...profile, ...payload })));
  };

  const validate = (data) => {
    const errors = {};

    if (!data.agreement) {
      errors.agreement = t('common:validation.notCheckedAgreement');
    }

    return errors;
  };

  const { values, errors, hasError, handleChange, handleSubmit } = useForm(onSubmit, {
    primaryFieldId: 1,
    agreement: false,
    language: languages.some(l => l.value === i18n.language) ? i18n.language : DEFAULT_LANGUAGE_CODE,
    nativeLanguage: DEFAULT_LANGUAGE_ID
  }, validate);

  if (hasSufficientSkeleton) {
    return <Page />;
  }

  const isProcessing = false;

  return (
    <Page
      centered
      withSplash
      initialContainer={false}
      helmetTitle={t('page.signup.title')}
    >
      <>
        <Subheader center>
          <TitleWithButton titleWrapper="div" variant="h3" title={t('page.oauth.signupTitle')}>
            <LanguageSwitch size="medium" language={i18n.language} onChange={handleChangeLanguage} />
          </TitleWithButton>
          <span>{t('page.oauth.signupSubtitle')}</span>
        </Subheader>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-6 col-lg-8 col-md-8 col-sm-12 col-xs-12 pt16">
              <Tip>
                {t('page.oauth.signupHint')}
              </Tip>
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                    <Select
                      fullWidth
                      label={t('common:userProfile.rolesSkills.primaryField')}
                      name="primaryFieldId"
                      options={fieldsList}
                      value={values.primaryFieldId}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                    <Select
                      fullWidth
                      label={t('common:form.nativeLanguage')}
                      name="nativeLanguage"
                      options={allLanguagesList}
                      value={values.nativeLanguage}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="row mb16">
                  <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                    <Select
                      fullWidth
                      label={t('common:form.language')}
                      name="language"
                      options={languagesList}
                      value={values.language}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <Checkbox
                      name="agreement"
                      error={errors.agreement}
                      value={values.agreement}
                      onChange={handleChange}
                      label={t('page.signup.agreement')}
                      fullWidth
                    />
                  </div>
                </div>
                <p className={styles.agreement}>
                  {t('page.signup.agreementLinks')}
                  {' '}
                  <a href={routes.tos} target="_blank" rel="noopener noreferrer">{t('footer.tos')}</a>
                  {', '}
                  <a href={routes.gdpr} target="_blank" rel="noopener noreferrer">{t('footer.gdpr')}</a>.
                </p>
                <div className="centerXs">
                  <Submit
                    title={t('common:button.send')}
                    disabled={hasError}
                    onClick={handleSubmit}
                    processing={isProcessing}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    </Page>
  );
};

export default Signup;
